import type { Instance } from '@nx/state';
import {
  FleetManagementTypes,
  consoleApi,
  fleetManagementApi,
  useActiveProject,
  useConnection,
  useOpsContext,
} from '@nx/state';
import { isNonEmptyString } from '@nx/stdlib';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

export const useInstanceSelectorProps = ({ dualMode = false }: { dualMode?: boolean }) => {
  const activeProject = useActiveProject();
  const { selectedInstanceId, setSelectedInstance } = useOpsContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { metadata: connectionMetadata } = useConnection();
  const [instances, setInstances] = useState<Instance[]>([]);
  const [deployment, setDeployment] = useState<FleetManagementTypes.DeploymentDetails | undefined>(undefined);

  const handleSelectInstanceId = useCallback(
    (instanceId?: string) => {
      if (isNonEmptyString(instanceId)) {
        setSelectedInstance(instanceId);
        setSearchParams((oldParams) => {
          const newParams = new URLSearchParams(oldParams);
          newParams.set('instance', instanceId);
          return newParams;
        });
      }
    },
    [setSearchParams, setSelectedInstance],
  );

  const listInstancesRes = consoleApi.useListInstancesQuery(activeProject.id, {
    refetchOnMountOrArgChange: true,
  });
  const [getInstance, getInstanceRes] = consoleApi.useLazyGetInstanceQuery();

  useEffect(() => {
    async function fetchDetailedInstances() {
      if (listInstancesRes.data && listInstancesRes.data.length > 0) {
        const promises = listInstancesRes.data.map(({ id }) => getInstance(id).unwrap());
        const detailedInstances = await Promise.all(promises);
        setInstances(detailedInstances);
      }
    }
    void fetchDetailedInstances();
  }, [getInstance, listInstancesRes.data]);

  const deploymentsRes = fleetManagementApi.useGetDeploymentsQuery(activeProject.id, {
    refetchOnMountOrArgChange: true,
    skip: !dualMode,
  });
  const [getDeploymentDetails] = fleetManagementApi.useLazyGetDeploymentDetailsQuery();

  useEffect(() => {
    async function fetchDetailedDeployments() {
      if (
        dualMode &&
        deploymentsRes.data &&
        deploymentsRes.data.length > 0 &&
        isNonEmptyString(selectedInstanceId) &&
        deploymentsRes.data.some(
          (fetchedDeployment) =>
            fetchedDeployment.deploymentId === selectedInstanceId && deployment?.deploymentId !== selectedInstanceId,
        )
      ) {
        const detailedDeployment = await getDeploymentDetails({
          projectId: activeProject.id,
          deploymentId: selectedInstanceId,
        }).unwrap();
        setDeployment(detailedDeployment);
      }
    }
    void fetchDetailedDeployments();
  }, [
    getDeploymentDetails,
    deploymentsRes.data,
    dualMode,
    activeProject.id,
    selectedInstanceId,
    deployment?.deploymentId,
  ]);

  useEffect(() => {
    if (
      listInstancesRes.data &&
      (!dualMode || !deploymentsRes.isLoading) &&
      (listInstancesRes.data.length > 0 || (dualMode && deploymentsRes.data && deploymentsRes.data.length > 0)) &&
      (selectedInstanceId === undefined ||
        (!listInstancesRes.data.find((i) => i.id === selectedInstanceId) &&
          !deploymentsRes.data?.find((i) => i.deploymentId === selectedInstanceId)))
    ) {
      const getInstanceFromConnection = () => {
        if (connectionMetadata?.source.type === 'aura') {
          return listInstancesRes.data?.find((i) => i.id === connectionMetadata.source.sourceId);
        }
        return null;
      };

      const getDeploymentFromConnection = () => {
        if (connectionMetadata?.source.type === 'fleet-management') {
          return deploymentsRes.data?.find((i) => i.deploymentId === connectionMetadata.source.sourceId);
        }
        return null;
      };

      const instanceIdFromSearch = searchParams.get('instance');
      const instanceFromSearch = listInstancesRes.data.find((i) => i.id === instanceIdFromSearch);
      const deploymentFromSearch = deploymentsRes.data?.find((i) => i.deploymentId === instanceIdFromSearch);
      const [firstInstance] = listInstancesRes.data;
      const monitoredDeployments = deploymentsRes.data
        ?.filter((data) => data.status !== FleetManagementTypes.Api.DEPLOYMENT_STATUS.UNMONITORED)
        .sort((a, b) => a.name.localeCompare(b.name));
      const [firstDeployment] = monitoredDeployments ?? [];
      handleSelectInstanceId(
        instanceFromSearch?.id ??
          deploymentFromSearch?.deploymentId ??
          getInstanceFromConnection()?.id ??
          getDeploymentFromConnection()?.deploymentId ??
          firstInstance?.id ??
          firstDeployment?.deploymentId,
      );
    }
  }, [
    listInstancesRes.data,
    deploymentsRes.data,
    deploymentsRes.isLoading,
    connectionMetadata?.source,
    handleSelectInstanceId,
    searchParams,
    selectedInstanceId,
    dualMode,
  ]);

  const [selectedInstance, selectedInstanceSummary] = useMemo(() => {
    return listInstancesRes.data && listInstancesRes.data.length > 0
      ? ([
          instances.find((i) => i.id === selectedInstanceId),
          listInstancesRes.data.find((i) => i.id === selectedInstanceId),
        ] as const)
      : [];
  }, [instances, listInstancesRes.data, selectedInstanceId]);

  const [selectedDeployment, selectedDeploymentSummary] = useMemo(() => {
    return deploymentsRes.data && deploymentsRes.data.length > 0
      ? ([
          deployment?.deploymentId === selectedInstanceId ? deployment : undefined,
          deploymentsRes.data.find((i) => i.deploymentId === selectedInstanceId),
        ] as const)
      : [];
  }, [deployment, deploymentsRes.data, selectedInstanceId]);

  return {
    isInstancesLoading:
      listInstancesRes.isLoading ||
      getInstanceRes.isLoading ||
      listInstancesRes.isFetching ||
      getInstanceRes.isFetching,
    isDeploymentsLoading: deploymentsRes.isLoading || deploymentsRes.isFetching,
    instanceSummaries: listInstancesRes.data ?? [],
    deployments: deploymentsRes.data ?? [],
    instances,
    selectedInstanceSummary,
    selectedInstance,
    selectedDeploymentSummary,
    selectedDeployment,
    setSelectedInstanceId: handleSelectInstanceId,
  };
};
